import React, { useState, useEffect } from 'react';
import '../CSS/Contact.css';

const Contact = () => {
  const [form, setForm] = useState({ name: '', contact: '', message: '' });
  const [submitStatus, setSubmitStatus] = useState(null);

  // Set dynamic meta tags for SEO
  useEffect(() => {
    document.title = 'Contact MUSCLECRETE - Get in Touch with PAC Corporation';
    
    const metaTags = {
      description: 'Contact MUSCLECRETE by PAC Corporation for inquiries about tile adhesives, concrete coatings, and construction solutions. Available Mon-Fri 9AM-6PM.',
      keywords: 'contact MUSCLECRETE, PAC Corporation contact, tile adhesive inquiry, concrete coating support, paccorporation.in',
    };

    // Update meta description
    let metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', metaTags.description);
    }

    // Update meta keywords
    let metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', metaTags.keywords);
    }

    // Update Open Graph tags
    const ogTags = {
      'og:title': 'Contact MUSCLECRETE - PAC Corporation',
      'og:description': 'Get in touch with MUSCLECRETE by PAC Corporation for tile adhesives, coatings, and construction solutions.',
      'og:url': 'https://www.paccorporation.in/contact',
    };

    Object.keys(ogTags).forEach(key => {
      let ogTag = document.querySelector(`meta[property="${key}"]`);
      if (ogTag) {
        ogTag.setAttribute('content', ogTags[key]);
      }
    });

    return () => {
      document.title = 'PAC Corporation';
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitStatus('loading');

    try {
      const response = await fetch(
        "https://websitequery-cdgrcnbdbub8e0gw.centralindia-01.azurewebsites.net/api/WebsiteQuery",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            email: form.contact,
            subject: `MUSCLECRETE Query from ${form.name}`,
            message: form.message,
          }),
        }
      );

      if (response.ok) {
        setSubmitStatus('success');
        setForm({ name: '', contact: '', message: '' });
        setTimeout(() => setSubmitStatus(null), 5000);
      } else {
        setSubmitStatus('error');
        setTimeout(() => setSubmitStatus(null), 5000);
      }
    } catch (error) {
      console.error("Error submitting query:", error);
      setSubmitStatus('error');
      setTimeout(() => setSubmitStatus(null), 5000);
    }
  };

  return (
    <div className="contact-page">
      <header className="contact-hero">
        <div className="contact-hero-content">
          <h1>Get in Touch with MUSCLECRETE</h1>
          <p>Have questions about our tile adhesives, coatings, or construction solutions? We're here to help!</p>
        </div>
      </header>

      <main className="contact-main">
        <div className="contact-container">
          <section className="contact-form-section">
            <div className="form-wrapper">
              <h2>Send us a Message</h2>
              <p className="form-intro">Fill out the form below and our team will respond within 24 hours</p>
              
              {submitStatus === 'success' && (
                <div className="status-message success">
                  ✓ Your query has been submitted successfully! We'll get back to you soon.
                </div>
              )}
              {submitStatus === 'error' && (
                <div className="status-message error">
                  ✗ Failed to submit. Please try again or call us directly.
                </div>
              )}

              <form className="contact-form" onSubmit={handleSubmit}>
                <div className="form-group">
                  <label htmlFor="name">Full Name *</label>
                  <input
                    id="name"
                    type="text"
                    placeholder="Your Name"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    required
                    aria-label="Full Name"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="contact">Email or Mobile *</label>
                  <input
                    id="contact"
                    type="text"
                    placeholder="your@email.com or +91-9XXXXXXXXX"
                    value={form.contact}
                    onChange={(e) => setForm({ ...form, contact: e.target.value })}
                    required
                    aria-label="Email or Mobile Number"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="message">Message *</label>
                  <textarea
                    id="message"
                    placeholder="Tell us about your MUSCLECRETE inquiry or how we can help..."
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    required
                    aria-label="Message"
                  />
                </div>
                <button type="submit" className="submit-btn" disabled={submitStatus === 'loading'}>
                  {submitStatus === 'loading' ? 'Submitting...' : 'Send Message'}
                </button>
              </form>
            </div>
          </section>

          <section className="contact-info-section">
            <div className="info-wrapper">
              <h2>Contact Information</h2>
              
              <div className="info-card">
                <div className="info-icon">📍</div>
                <h3>Address</h3>
                <p>
                  Khasara No 667, Kalyanpur URF Narsan Kalan<br />
                  Haridwar, Uttarakhand 249404<br />
                  India
                </p>
              </div>

              <div className="info-card">
                <div className="info-icon">📞</div>
                <h3>Customer Care</h3>
                <p>
                  <a href="tel:+919045799140" className="contact-link">+91-9045799140</a><br />
                  <a href="tel:+917599224268" className="contact-link">+91-7599-22-4268</a>
                </p>
              </div>

              <div className="info-card">
                <div className="info-icon">✉️</div>
                <h3>Email</h3>
                <p>
                  <a href="mailto:info@paccorporation.in" className="contact-link">info@paccorporation.in</a>
                </p>
              </div>

              <div className="info-card">
                <div className="info-icon">🕐</div>
                <h3>Business Hours</h3>
                <p>
                  Monday – Friday: 9:00 AM – 6:00 PM<br />
                  Saturday & Sunday: Closed
                </p>
              </div>

              <div className="info-highlight">
                <h3>Why Choose MUSCLECRETE?</h3>
                <ul>
                  <li>Premium tile adhesives and grouts</li>
                  <li>Professional-grade construction solutions</li>
                  <li>Trusted by builders nationwide</li>
                  <li>Expert support and guidance</li>
                  <li>Fast delivery and response</li>
                </ul>
              </div>
            </div>
          </section>
        </div>

        <section className="map-section">
          <h2>Visit Us</h2>
          <div className="map-container">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3344.0832653854653!2d78.1355!3d29.9353!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x390a3c7f7f7f7f7f%3A0x0!2sPAC%20Corporation!5e0!3m2!1sen!2sin!4v1234567890"
              width="100%"
              height="450"
              style={{ border: 0, borderRadius: '8px' }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="PAC Corporation MUSCLECRETE Location"
            ></iframe>
          </div>
        </section>
      </main>
    </div>
  );
};

export default Contact;